import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { HumisLogo } from '@/components/brand/HumisLogo'
import { apiClient } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'

/**
 * Where an invitation link lands: choose a password, then sign in.
 *
 * <p>Invitations were created with a token and nowhere to use it — no page and no endpoint —
 * so an invited person had a link to nothing and an account that stayed "invited" for ever.
 * The page asks for the password twice because there is no "forgot password" to fall back on
 * yet, and a typo here would lock somebody out of an account they have never signed in to.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  async function submit() {
    setError(null)
    setPasswordError(null)
    if (password !== confirm) {
      setPasswordError('The two passwords are different.')
      return
    }
    setSaving(true)
    try {
      await apiClient.post('/v1/auth/accept-invite', { token, password })
      setDone(true)
    } catch (caught) {
      const problem = formProblem(caught, 'That did not work. Try again, or ask for a new invitation.')
      setPasswordError(problem.fields.password ?? null)
      setError(problem.fields.password ? null : problem.summary)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-[var(--color-page)] p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <HumisLogo variant="full" alt="HUMIS" className="h-16 w-auto max-w-full" />
        </div>

        <div className="hrms-card p-6">
          <h1 className="text-lg font-semibold text-[var(--color-heading)]">Set up your account</h1>

          {!token && (
            <p role="alert" className="mt-3 text-sm text-[var(--color-danger)]">
              This link is incomplete. Open the whole link from your invitation, or ask for a new one.
            </p>
          )}

          {done && (
            <div className="mt-4 space-y-4">
              <p className="flex items-start gap-2 text-sm text-[var(--color-success)]">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                Your password is set. Sign in with your email and the password you just chose.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          )}

          {token && !done && (
            <form
              className="mt-4 space-y-4"
              noValidate
              onSubmit={(event) => { event.preventDefault(); void submit() }}
            >
              <p className="text-sm text-[var(--color-muted)]">
                Choose the password you will sign in with.
              </p>
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-red)] px-3 py-2 text-sm text-[var(--color-danger)]"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="new-password" required>New password</Label>
                <Input
                  id="new-password" type="password" autoComplete="new-password" required
                  value={password} invalid={Boolean(passwordError)}
                  aria-describedby={passwordError ? 'new-password-error' : undefined}
                  onChange={(event) => setPassword(event.target.value)}
                />
                {passwordError && (
                  <p id="new-password-error" className="text-xs text-[var(--color-danger)]">
                    {passwordError}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" required>Type it again</Label>
                <Input
                  id="confirm-password" type="password" autoComplete="new-password" required
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={saving || !password || !confirm}>
                {saving ? 'Saving…' : 'Set password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
